/**
 * Base class for all Snowplow analytics events.
 * Provides a standardized structure for event tracking with a schema
 * and data payload. All specific event types should extend this class.
 *
 * @abstract
 */
class Event {
  /**
   * Creates a new event with the specified Snowplow schema.
   * @param {string} schema - The Iglu schema URI that defines the event structure
   */
  constructor(schema) {
    this.schema = schema;
    this.data = {};
  }
}

/**
 * Tracks user interactions with dashboard date filtering.
 */
export class FilterDashboard extends Event {
  /**
   * Creates a new filter dashboard event with specified date range.
   * @param {string} startDate - The start date of the filter.
   * @param {string} endDate - The end date of the filter.
   */
  constructor(startDate, endDate) {
    super("iglu:com.transfrvr/filter_dashboard/jsonschema/1-0-0");
    this.data.start_date = startDate;
    this.data.end_date = endDate;
  }
}

/**
 * Tracks user logout events with the deauthentication method and if user was impersonator.
 */
export class DeauthenticateUser extends Event {
  /**
   * Creates a new deauthentication method event with the specified deauthentication method
   * which can either be "core-platform", "impersonation", or "SSO"
   * @param {string} deauthenticationMethod - Specifies the method used for deauthentication.
   */
  constructor(deauthenticationMethod) {
    super("iglu:com.transfrvr/deauthenticate_user/jsonschema/3-0-0");
    this.data = {
      deauthentication_method: deauthenticationMethod,
    };
  }
}

/**
 * Tracks a user's first sign-in to the dashboard. Mirrors the event
 * core-platform emits for a transfer-token claim: no data of its own.
 */
export class FirstEntry extends Event {
  constructor() {
    super("iglu:com.transfrvr/first_entry/jsonschema/1-0-0");
  }
}

/**
 * Tracks whenever the user navigates to on the Organization tab on the dashboard.
 */
export class ViewOrganizationList extends Event {
  /**
   * Creates a new ViewOrganizationList event.
   */
  constructor() {
    super("iglu:com.transfrvr/view_organization_list/jsonschema/1-0-0");
  }
}

/**
 * Tracks whenever the user navigates to on the Classrooms tab on the dashboard.
 */
export class ViewClassroomList extends Event {
  /**
   * Creates an instance of a ViewClassroomList event.
   */
  constructor() {
    super("iglu:com.transfrvr/view_classroom_list/jsonschema/1-0-0");
  }
}

/**
 * Tracks whenever the user navigates to the Users tab on the dashboard.
 */
export class ViewUserList extends Event {
  /**
   * Creates an isntance of a ViewUserList event.
   * @param {string} viewName e - The name of the selected tab the user is viewing.
   * @param {string} viewedAppId - The ID of the product the user is interacting with (optional).
   */
  constructor(viewName, viewedAppId = "") {
    super("iglu:com.transfrvr/view_user_list/jsonschema/1-0-0");
    this.data = {
      view_name: viewName,
      viewed_app_id: viewedAppId,
    };
  }
}

/**
 * Tracks whenever the user navigates to the Catalog tab on the dashboard.
 */
export class ViewCatalog extends Event {
  /**
   * Creates an instance of a ViewCatalog event.
   */
  constructor(viewedAppId = "") {
    super("iglu:com.transfrvr/view_catalog/jsonschema/1-0-0");
    this.data.viewed_app_id = viewedAppId;
  }
}

/**
 * Tracks whenever the user navigates to the Support tab on the dashboard.
 */
export class ViewSupport extends Event {
  /**
   * Creates an instance of a ViewSupport event.
   */
  constructor(viewedAppId = "") {
    super("iglu:com.transfrvr/view_support/jsonschema/1-0-0");
    this.data.viewed_app_id = viewedAppId;
  }
}

/**
 * Tracks whenever the user navigates to a specific classroom and changes active tab.
 */
export class ViewClassroom extends Event {
  /**
   * Creates an instance of a ViewClassroom event.
   */
  constructor(viewName, viewedAppId, viewedClassroomId) {
    super("iglu:com.transfrvr/view_classroom/jsonschema/2-0-0");
    this.data = {
      view_name: viewName,
      viewed_app_id: viewedAppId,
      viewed_classroom_id: viewedClassroomId,
    };
  }
}

/**
 * Tracks whenever the user navigates to a Trainee Details page and switches active classroom tab.
 */
export class ViewUser extends Event {
  /**
   * Creates an instance of a ViewUser event.
   */
  constructor(viewName, viewedAppId, viewedClassroomId, viewedUserId) {
    super("iglu:com.transfrvr/view_user/jsonschema/1-0-0");
    this.data = {
      view_name: viewName,
      viewed_app_id: viewedAppId,
      viewed_classroom_id: viewedClassroomId,
      viewed_user_id: viewedUserId,
    };
  }
}

/**
 * Tracks whenever the user navigates to the profile page on the dashboard.
 */
export class ViewProfile extends Event {
  /**
   * Creates an instance of a ViewProfile event.
   */
  constructor(viewedAppId = "") {
    super("iglu:com.transfrvr/view_profile/jsonschema/1-0-0");
    this.data.viewed_app_id = viewedAppId;
  }
}

/**
 * Tracks users in Mission Control screen, userId, classroomId, and instructorID
 */
export class TrackMissionControl extends Event {
  /**
   * Creates an instance of a TrackMissionControl event.
   */
  constructor(viewedUserId, viewedClassroomId) {
    super("iglu:com.transfrvr/view_mission_control_tile/jsonschema/1-0-0");
    this.data = {
      viewed_user_id: viewedUserId,
      viewed_classroom_id: viewedClassroomId,
    };
  }
}
export class TrackMissionControlTabClick extends Event {
  constructor(classroomId, currentUserId) {
    super("iglu:com.transfrvr/click_mission_control_tab/jsonschema/1-0-0");
    this.data = {
      clicked_classroom_id: classroomId,
    };
  }
}

/**
 * Tracks whenever the user clicks the launch Trek button on the dashboard.
 */
export class TrackLaunchTrekButtonClick extends Event {
  constructor() {
    super("iglu:com.transfrvr/click_launch_trek_button/jsonschema/1-0-0");
  }
}

/**
 * Tracks whenever the user clicks the network connectivity link.
 */
export class TrackTestYourNetworkConnectivityClick extends Event {
  constructor() {
    super(
      "iglu:com.transfrvr/click_test_network_connectivity_link/jsonschema/1-0-0"
    );
    this.data = {
      source: "dashboard",
    };
  }
}

/**
 * Tracks thumbs up/down feedback on an AI Curriculum recommendation run
 * (ENG-23190). Fired on every select and change — including clearing the
 * rating — so up→down switches are captured, not just the first click.
 */
export class TrackAiCurriculumRecommendationFeedback extends Event {
  /**
   * @param {("up"|"down"|null)} rating - The new rating; null when cleared.
   * @param {string} entryPoint - AICA entry point: "quick_start",
   *   "state_standards", or "upload".
   * @param {string} sessionId - The AI Curriculum session the run belongs to.
   * @param {number|null} runVersion - Recommendations run version from
   *   GET /recommendations, when known.
   */
  constructor(rating, entryPoint, sessionId, runVersion) {
    super("iglu:com.transfrvr/aica_recommendation_feedback/jsonschema/1-0-0");
    this.data = {
      rating,
      entry_point: entryPoint,
      session_id: sessionId,
      run_version: runVersion ?? null,
    };
  }
}

const setupChecklistSchema = (name) =>
  `iglu:com.transfrvr/setup_checklist_${name}/jsonschema/1-0-0`;

export class TrackSetupChecklistViewed extends Event {
  constructor(organizationCode) {
    super(setupChecklistSchema("viewed"));
    this.data = { organization_code: organizationCode };
  }
}

export class TrackSetupChecklistStepExpanded extends Event {
  constructor(organizationCode, stepKey) {
    super(setupChecklistSchema("step_expanded"));
    this.data = { organization_code: organizationCode, step_key: stepKey };
  }
}

/**
 * A step or sub-step observed transitioning to COMPLETE.
 */
export class TrackSetupChecklistItemCompleted extends Event {
  /**
   * @param {string} organizationCode
   * @param {object} item
   * @param {string} item.stepKey
   * @param {string|null} item.subStepKey - null when the step itself completed.
   * @param {string|null} item.kind - Sub-step kind; null for a step.
   * @param {string|null} item.completedVia
   */
  constructor(
    organizationCode,
    { stepKey, subStepKey = null, kind = null, completedVia = null }
  ) {
    super(setupChecklistSchema("item_completed"));
    this.data = {
      organization_code: organizationCode,
      step_key: stepKey,
      sub_step_key: subStepKey,
      kind,
      completed_via: completedVia,
    };
  }
}

export class TrackSetupChecklistAttestationRecorded extends Event {
  constructor(organizationCode, key) {
    super(setupChecklistSchema("attestation_recorded"));
    this.data = { organization_code: organizationCode, key };
  }
}

export class TrackSetupChecklistAttestationCleared extends Event {
  constructor(organizationCode, key) {
    super(setupChecklistSchema("attestation_cleared"));
    this.data = { organization_code: organizationCode, key };
  }
}

export class TrackSetupChecklistQuestionAnswered extends Event {
  constructor(organizationCode, key, option) {
    super(setupChecklistSchema("question_answered"));
    this.data = { organization_code: organizationCode, key, option };
  }
}

export class TrackSetupChecklistActionLaunched extends Event {
  /**
   * @param {string} organizationCode
   * @param {object} action
   * @param {string} action.stepKey
   * @param {string} action.subStepKey
   * @param {string} action.surface
   */
  constructor(organizationCode, { stepKey, subStepKey, surface }) {
    super(setupChecklistSchema("action_launched"));
    this.data = {
      organization_code: organizationCode,
      step_key: stepKey,
      sub_step_key: subStepKey,
      surface,
    };
  }
}
